/**
 * Formatting helpers.
 *
 * The important rule here concerns pickup appointments. The server already
 * renders them in the *library's* timezone (`pickup_at_local`), because an
 * appointment is a wall-clock time at a physical desk. Passing that value
 * through the browser's default date formatting would silently convert it into
 * the viewer's own timezone -- so a member travelling abroad would be told the
 * wrong time. Every function below that touches an appointment therefore pins
 * the timezone explicitly.
 *
 * Second rule: under an `ar` locale `Intl` emits Arabic-Indic digits (١٧:٣٠).
 * That is correct typography but unhelpful at a pickup desk where staff read a
 * printed list, so the numbering system is pinned to Latin digits.
 */

import type { Language } from '@/types/api'

/** Arabic with Latin digits, so times and counts stay machine-readable. */
const localeFor = (language: Language): string =>
  language === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB'

const safeDate = (value: string | null | undefined): Date | null => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Format an appointment in the library's timezone.
 *
 * Pass the UTC `pickup_at` together with the `library_timezone` from the same
 * payload -- never the browser's zone.
 */
export const formatAppointment = (
  utcInstant: string | null | undefined,
  timeZone: string,
  language: Language,
  options: Intl.DateTimeFormatOptions = {},
): string => {
  const date = safeDate(utcInstant)
  if (!date) return ''
  return new Intl.DateTimeFormat(localeFor(language), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone,
    ...options,
  }).format(date)
}

/** Just the clock time of an appointment, in the library's timezone. */
export const formatAppointmentTime = (
  utcInstant: string | null | undefined,
  timeZone: string,
  language: Language,
): string =>
  formatAppointment(utcInstant, timeZone, language, {
    weekday: undefined,
    day: undefined,
    month: undefined,
    year: undefined,
  })

/** Just the date of an appointment, in the library's timezone. */
export const formatAppointmentDate = (
  utcInstant: string | null | undefined,
  timeZone: string,
  language: Language,
): string =>
  formatAppointment(utcInstant, timeZone, language, {
    hour: undefined,
    minute: undefined,
  })

/**
 * A plain date with no timezone significance (created_at, date_joined, ...).
 * These may safely render in the viewer's own zone.
 */
export const formatDate = (value: string | null | undefined, language: Language): string => {
  const date = safeDate(value)
  if (!date) return ''
  return new Intl.DateTimeFormat(localeFor(language), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

export const formatDateTime = (value: string | null | undefined, language: Language): string => {
  const date = safeDate(value)
  if (!date) return ''
  return new Intl.DateTimeFormat(localeFor(language), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)
}

/** "in 3 days" / "2 hours ago", for hold windows and due dates. */
export const formatRelative = (value: string | null | undefined, language: Language): string => {
  const date = safeDate(value)
  if (!date) return ''
  const formatter = new Intl.RelativeTimeFormat(localeFor(language), { numeric: 'auto' })
  const deltaSeconds = (date.getTime() - Date.now()) / 1000

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
  ]
  for (const [unit, seconds] of units) {
    if (Math.abs(deltaSeconds) >= seconds) {
      return formatter.format(Math.round(deltaSeconds / seconds), unit)
    }
  }
  return formatter.format(Math.round(deltaSeconds), 'second')
}

export const formatNumber = (value: number, language: Language): string =>
  new Intl.NumberFormat(localeFor(language)).format(value)

/** "09:00" as stored by the API; already display-ready, kept for symmetry. */
export const formatClock = (value: string): string => value.slice(0, 5)

export const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
